import { useCallback, useEffect, useMemo, useState } from 'react';
import StatusBadge from '@/components/base/StatusBadge';
import OrderDetailDrawer from './components/OrderDetailDrawer';
import VoidedBills from './components/VoidedBills';
import { fetchOrders, subscribeOrders, type Order } from '@/lib/data';
import { readCache, writeCache } from '@/lib/cache';
import { useRestaurant } from '@/lib/session';

type OrderTypeFilter = 'All' | 'Dine-in' | 'Takeaway' | 'Swiggy' | 'Zomato';
type OrderStatusFilter = 'All' | 'received' | 'preparing' | 'ready' | 'served' | 'cancelled';

function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}

export default function OrdersPage() {
  const restaurant = useRestaurant();
  // Seed from cache so returning to Orders shows today's list instantly while
  // the fetch + realtime subscription below refresh it.
  const [orders, setOrders] = useState<Order[] | null>(
    () => readCache<Order[]>(`orders.today:${restaurant.id}`) ?? null,
  );
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<OrderTypeFilter>('All');
  const [statusFilter, setStatusFilter] = useState<OrderStatusFilter>('All');
  const [sortKey, setSortKey] = useState<'placedAt' | 'total' | 'id'>('placedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // 'live' = today's orders (default); 'voided' = the recycle bin of bills a
  // manager voided on the POS (hidden from the POS, restorable here).
  const [view, setView] = useState<'live' | 'voided'>('live');

  const typeFilters: OrderTypeFilter[] = ['All', 'Dine-in', 'Takeaway', 'Swiggy', 'Zomato'];
  const statusFilters: OrderStatusFilter[] = ['All', 'received', 'preparing', 'ready', 'served', 'cancelled'];

  const loadOrders = useCallback(async () => {
    try {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      const rows = await fetchOrders(restaurant.id, {
        from: start.toISOString(),
        to: end.toISOString(),
      });
      writeCache(`orders.today:${restaurant.id}`, rows);
      setOrders(rows);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load orders.');
    }
  }, [restaurant.id]);

  useEffect(() => {
    loadOrders();
    const unsubscribe = subscribeOrders(restaurant.id, loadOrders);
    return unsubscribe;
  }, [restaurant.id, loadOrders]);

  const handleOrderUpdated = useCallback((id: string, patch: Partial<Order>) => {
    setOrders((prev) => (prev ? prev.map((o) => (o.id === id ? { ...o, ...patch } : o)) : prev));
    setSelectedOrder((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));
  }, []);

  const filteredOrders = useMemo(() => {
    let result = [...(orders ?? [])];

    if (typeFilter !== 'All') {
      result = result.filter((o) => o.type === typeFilter);
    }
    if (statusFilter !== 'All') {
      result = result.filter((o) => o.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (o) =>
          o.id.toLowerCase().includes(q) ||
          o.customer.toLowerCase().includes(q) ||
          (o.tableId ?? '').toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'placedAt') cmp = a.placedAt.localeCompare(b.placedAt);
      else if (sortKey === 'total') cmp = a.total - b.total;
      else cmp = a.id.localeCompare(b.id);
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [orders, search, typeFilter, statusFilter, sortKey, sortDir]);

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const openDrawer = (order: Order) => {
    setSelectedOrder(order);
    setDrawerOpen(true);
  };

  const loading = orders === null && !error;
  const activeCount = (orders ?? []).filter((o) => o.status !== 'cancelled' && o.status !== 'served').length;

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-foreground-800">Orders</h2>
          <p className="text-sm text-foreground-500 mt-0.5">
            {view === 'voided'
              ? 'Bills voided on the POS — review or restore'
              : loading
                ? 'Loading orders…'
                : `${(orders ?? []).length} orders today · ${activeCount} active`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Live orders vs the voided recycle bin */}
          <div className="inline-flex rounded-lg border border-background-200 bg-background-50 p-0.5">
            {(['live', 'voided'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
                  view === v ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-800'
                }`}
              >
                {v === 'live' ? 'Orders' : 'Voided bin'}
              </button>
            ))}
          </div>
          {view === 'live' && (
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
              <input
                type="text"
                placeholder="Search orders, tables, customers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 w-64"
              />
            </div>
          )}
        </div>
      </div>

      {view === 'voided' && <VoidedBills restaurantId={restaurant.id} />}

      {view === 'live' && (
      <>
      {/* Type filter tabs */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {typeFilters.map((f) => (
          <button
            key={f}
            onClick={() => setTypeFilter(f)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
              typeFilter === f
                ? 'bg-primary-500 text-background-50 border-primary-500'
                : 'bg-background-50 text-foreground-600 border-background-200 hover:border-background-300'
            }`}
          >
            {f}
          </button>
        ))}
        <span className="w-px h-5 bg-background-300 mx-1"></span>
        {statusFilters.map((f) => (
          <button
            key={f}
            onClick={() => setStatusFilter(f)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
              statusFilter === f
                ? 'bg-secondary-500 text-background-50 border-secondary-500'
                : 'bg-background-50 text-foreground-600 border-background-200 hover:border-background-300'
            }`}
          >
            {f === 'All' ? 'All Status' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-100 border-b border-background-200">
              <tr>
                <th
                  className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider cursor-pointer whitespace-nowrap hover:text-foreground-700"
                  onClick={() => toggleSort('id')}
                >
                  Order ID {sortKey === 'id' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Table</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Items</th>
                <th
                  className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider cursor-pointer whitespace-nowrap hover:text-foreground-700"
                  onClick={() => toggleSort('total')}
                >
                  Amount {sortKey === 'total' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Payment</th>
                <th
                  className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider cursor-pointer whitespace-nowrap hover:text-foreground-700"
                  onClick={() => toggleSort('placedAt')}
                >
                  Time {sortKey === 'placedAt' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {filteredOrders.map((order) => (
                <tr
                  key={order.id}
                  onClick={() => openDrawer(order)}
                  className="hover:bg-background-100/50 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <span className="font-medium text-foreground-800">{shortId(order.id)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                      {order.type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm ${!order.tableId ? 'text-foreground-400' : 'text-foreground-700 font-medium'}`}>
                      {order.tableId ?? '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <span className="text-sm text-foreground-700">
                        {order.itemCount} {order.itemCount === 1 ? 'item' : 'items'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-medium text-foreground-800 whitespace-nowrap">₹{order.total.toFixed(2)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-600 whitespace-nowrap">{order.paymentMode}</span>
                      <StatusBadge status={order.paymentStatus} />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-foreground-500 whitespace-nowrap">
                      {formatTime(order.placedAt)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (
          <div className="py-16 text-center">
            <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin"></i>
            <p className="text-sm text-foreground-500 mt-3">Loading today's orders…</p>
          </div>
        )}

        {error && !loading && (
          <div className="py-16 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-xl text-accent-500"></i>
            </div>
            <p className="text-sm text-foreground-500">{error}</p>
            <button
              onClick={loadOrders}
              className="mt-3 px-4 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && filteredOrders.length === 0 && (
          <div className="py-16 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-search-line text-xl text-foreground-300"></i>
            </div>
            <p className="text-sm text-foreground-500">
              {(orders ?? []).length === 0 ? 'No orders yet today' : 'No orders match your filters'}
            </p>
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      <OrderDetailDrawer
        order={selectedOrder}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedOrder(null); }}
        onUpdated={handleOrderUpdated}
      />
      </>
      )}
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

